/**
 * CustomLayerRenderer — renders a single user-added custom layer inside a Remotion composition.
 *
 * Supports four layer types:
 *  • text      — styled text box with optional background
 *  • image     — <Img> with objectFit
 *  • video     — muted <Video> with objectFit
 *  • animation — decorative graphic element (gradient / color block) with entrance animation
 *
 * Entrance animations (animationType) are driven by Remotion spring/interpolate
 * and respect animationDelay (frames before animation starts).
 */
import React from 'react';
import { Img, Video, useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';
import type { CustomLayer } from '../types';

// ─── Animation hook ───────────────────────────────────────────

function useEntrance(layer: CustomLayer): { opacity: number; transform: string } {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const delay = layer.animationDelay ?? 0;
  const dur   = layer.animationDuration ?? 20;
  const f     = Math.max(0, frame - delay);
  const type  = layer.animationType ?? 'none';

  // No animation — just pass through opacity
  if (type === 'none' || !type) return { opacity: layer.opacity, transform: 'none' };

  // Pulse is frame-continuous, not an entrance
  if (type === 'pulse') {
    const pulse = 1 + Math.sin(frame * 0.18) * 0.04;
    return { opacity: layer.opacity, transform: `scale(${pulse})` };
  }

  const sp = spring({ frame: f, fps, config: { damping: 18, mass: 0.8, stiffness: 120 }, durationInFrames: dur });
  const fade = Math.min(1, f / Math.max(1, dur / 3)) * layer.opacity;

  switch (type) {
    case 'fade-in':
      return { opacity: interpolate(f, [0, dur / 2], [0, layer.opacity], { extrapolateRight: 'clamp' }), transform: 'none' };
    case 'slide-left':
      return { opacity: fade, transform: `translateX(${(1 - sp) * -240}px)` };
    case 'slide-right':
      return { opacity: fade, transform: `translateX(${(1 - sp) * 240}px)` };
    case 'slide-up':
      return { opacity: fade, transform: `translateY(${(1 - sp) * 120}px)` };
    case 'slide-down':
      return { opacity: fade, transform: `translateY(${(1 - sp) * -120}px)` };
    case 'zoom-in':
      return { opacity: fade, transform: `scale(${sp})` };
    default:
      return { opacity: layer.opacity, transform: 'none' };
  }
}

// ─── Main renderer ────────────────────────────────────────────

export const CustomLayerRenderer: React.FC<{ layer: CustomLayer }> = ({ layer }) => {
  const { opacity, transform } = useEntrance(layer);

  if (!layer.visible) return null;

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left:   layer.x,
    top:    layer.y,
    width:  layer.w,
    height: layer.h,
    zIndex: layer.zIndex,
    opacity,
    transform: transform !== 'none' ? transform : undefined,
    transformOrigin: 'center center',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };

  // ── Text ───────────────────────────────────────────
  if (layer.type === 'text') {
    return (
      <div style={{
        ...baseStyle,
        background: layer.bgColor ?? 'transparent',
        display:    'flex',
        alignItems: 'center',
        padding:    '10px 16px',
      }}>
        <span style={{
          fontFamily:  layer.fontFamily ?? 'Roboto, sans-serif',
          fontSize:    layer.fontSize   ?? 52,
          fontWeight:  layer.fontWeight ?? 700,
          color:       layer.color      ?? '#ffffff',
          textAlign:   layer.textAlign  ?? 'left',
          lineHeight:  1.2,
          wordBreak:   'break-word',
          width:       '100%',
          display:     'block',
        }}>
          {layer.text ?? ''}
        </span>
      </div>
    );
  }

  // ── Image ──────────────────────────────────────────
  if (layer.type === 'image') {
    if (!layer.src) {
      return (
        <div style={{ ...baseStyle, border: '2px dashed #334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#334155', fontSize: 18 }}>🖼</span>
        </div>
      );
    }
    return (
      <div style={baseStyle}>
        <Img src={layer.src} style={{ width: '100%', height: '100%', objectFit: layer.objectFit ?? 'cover', display: 'block' }} />
      </div>
    );
  }

  // ── Video ──────────────────────────────────────────
  if (layer.type === 'video') {
    if (!layer.src) {
      return <div style={{ ...baseStyle, border: '2px dashed #334155' }} />;
    }
    return (
      <div style={baseStyle}>
        <Video src={layer.src} style={{ width: '100%', height: '100%', objectFit: layer.objectFit ?? 'cover' }} muted />
      </div>
    );
  }

  // ── Animation (decorative graphic element) ─────────
  if (layer.type === 'animation') {
    const bg = layer.bgColor ?? 'rgba(124,58,237,0.85)';
    return (
      <div style={{
        ...baseStyle,
        background:   bg,
        borderRadius: 4,
      }} />
    );
  }

  return null;
};
